import { getAuthHeaders, handleAuthError } from './../utils/authUtils';

export const flightApiService = {
  searchFlights: async (userId, searchParams = {}, selectedFields = [], loadAllRecords = false, filters = {}, sessionId = null) => {
    // Build parameters array from searchParams
    const parameters = Object.entries(searchParams)
      .filter(([_, value]) => value !== undefined && value !== '')
      .map(([name, value]) => ({ name, value }));

    // Add filterFields as an array parameter (similar to fields)
    if (filters && Object.keys(filters).length > 0) {
      const filterFieldsArray = Object.entries(filters).map(([fieldName, filterConfig]) => ({
        field: fieldName,
        ...filterConfig
      }));
      
      parameters.push({
        name: "filterFields",
        value: filterFieldsArray
      });
    }

    // Build request payload with session support
    const payload = {
      userId,
      parameters,
      loadAll: loadAllRecords,
      sessionId: sessionId || undefined // Include sessionId if available
    };

    console.log('API Request Payload:', {
      url: `${process.env.REACT_APP_API_BASE_URL}/api/schedules/query`,
      payload,
      selectedFields,
      filtersCount: Object.keys(filters).length,
      hasSessionId: !!sessionId
    });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/schedules/query`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      console.log('API Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response Data:', {
        dataLength: data?.data?.length || 0,
        totalRecords: data?.totalRecords,
        returnedRecords: data?.returnedRecords,
        hasMore: data?.hasMore,
        quotaOver: data?.quotaOver,
        switchedToPermanentQuota: data?.switchedToPermanentQuota,
        usingPermanentQuota: data?.usingPermanentQuota,
        previouslyUsingPermanent: data?.previouslyUsingPermanent,
        rowsFromExpiring: data?.rowsFromExpiring,
        rowsFromPermanent: data?.rowsFromPermanent,
        isLimited: data?.isLimited,
        loadAll: data?.loadAll,
        sessionId: data?.sessionId, // NEW: Log session ID
        downloadUrl: data?.downloadUrl,
        fileName: data?.fileName,
        errorMessage: data?.errorMessage
      });
      
      if (!data || typeof data !== 'object') {
        console.warn('Invalid API response structure:', data);
        return { 
          data: [], 
          totalRecords: 0, 
          returnedRecords: 0, 
          hasMore: false, 
          isLimited: false,
          sessionId: null 
        };
      }
      
      // Return the full API response structure with session ID
      return {
        data: data.data || [],
        totalRecords: data.totalRecords || 0,
        returnedRecords: data.returnedRecords || 0,
        hasMore: data.hasMore || false,
        quotaOver: data.quotaOver || false,
        switchedToPermanentQuota: data.switchedToPermanentQuota || false,
        usingPermanentQuota: data.usingPermanentQuota || false,
        previouslyUsingPermanent: data.previouslyUsingPermanent || false,
        rowsFromExpiring: data.rowsFromExpiring || 0,
        rowsFromPermanent: data.rowsFromPermanent || 0,
        isLimited: data.isLimited || false,
        limit: data.limit || 0,
        loadAll: data.loadAll || false,
        sessionId: data.sessionId || null, // NEW: Return session ID
        downloadUrl: data.downloadUrl || null,
        fileName: data.fileName || null,
        errorMessage: data.errorMessage || null
      };
    } catch (error) {
      console.error('API searchFlights error:', {
        message: error.message,
        stack: error.stack,
        payload,
        filters,
        sessionId
      });
      throw error;
    }
  }
};

export const getCountries = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/countries`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleAuthError(response);
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText || response.statusText}`);
    }

    const countries = await response.json();
    
    // Transform API response to match expected format { code, name }
    return countries.map(country => ({
      code: country.countryName,
      name: country.countryName,
      id: country.id,
      region: country.region
    }));
  } catch (error) {
    console.error('Error fetching countries:', error);
    throw error;
  }
};

export const getRegions = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/countries/regions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleAuthError(response);
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText || response.statusText}`);
    }

    const regions = await response.json();
    
    // Transform API response to match expected format { code, name }
    return regions.map(region => ({
      code: region,
      name: region
    }));
  } catch (error) {
    console.error('Error fetching regions:', error);
    throw error;
  }
};

// Additional helper functions for country/region operations
export const getCountriesByRegion = async (regionName) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/countries/region/${encodeURIComponent(regionName)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleAuthError(response);
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText || response.statusText}`);
    }

    const countries = await response.json();
    
    return countries.map(country => ({
      code: country.countryName,
      name: country.countryName,
      id: country.id,
      region: country.region
    }));
  } catch (error) {
    console.error(`Error fetching countries for region ${regionName}:`, error);
    throw error;
  }
};

export const getCountryByName = async (countryName) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/countries/name/${encodeURIComponent(countryName)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleAuthError(response);
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText || response.statusText}`);
    }

    const country = await response.json();
    
    return {
      code: country.countryName,
      name: country.countryName,
      id: country.id,
      region: country.region
    };
  } catch (error) {
    console.error(`Error fetching country ${countryName}:`, error);
    throw error;
  }
};